// src/components/Layout/Header.jsx
import { Menu, Bell, User, LogOut, Settings, X, CheckCircle, AlertTriangle, Info, Sun, Moon, Plus, Save, Trash2, Check } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useStore from '../../store/useStore';
import apiClient from '../../services/api';
import {
  getProfiles,
  saveProfile,
  deleteProfile,
  getActiveProfileId,
  setActiveProfileId,
} from '../../utils/connectorConfig';

function registerAsDataPoolParticipant(profile) {
  if (!profile.providerEdcUrl && !profile.providerDspUrl) return;
  apiClient.post('/data-pool/register-mine', {
    name: profile.name,
    providerEdcUrl: profile.providerEdcUrl,
    providerDspUrl: profile.providerDspUrl,
  }).catch(() => { /* best-effort */ });
}

const COLORS = [
  { cls: 'bg-blue-600',    hex: '#2563eb' },
  { cls: 'bg-emerald-600', hex: '#059669' },
  { cls: 'bg-purple-600',  hex: '#9333ea' },
  { cls: 'bg-orange-500',  hex: '#f97316' },
  { cls: 'bg-pink-600',    hex: '#db2777' },
  { cls: 'bg-teal-600',    hex: '#0d9488' },
  { cls: 'bg-red-600',     hex: '#dc2626' },
  { cls: 'bg-indigo-500',  hex: '#6366f1' },
];

function getInitials(name) {
  return name.trim().split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

function buildProfileData(id, name, colorIdx, providerUrl, consumerUrl, apiKey, providerDspUrl, consumerDspUrl) {
  const c = COLORS[colorIdx] || COLORS[0];
  return {
    id, name,
    color: c.cls,
    providerEdcUrl: providerUrl, provider_url: providerUrl,
    assetsProviderUrl: providerUrl, policiesProviderUrl: providerUrl,
    providerApiKey: apiKey, edc_api_key: apiKey,
    assetsApiKey: apiKey, policiesApiKey: apiKey,
    consumerEdcUrl: consumerUrl, consumer_url: consumerUrl,
    consumerApiKey: apiKey,
    providerDspUrl: providerDspUrl || '',
    consumerDspUrl: consumerDspUrl || '',
  };
}

const inputCls = 'w-full px-3 py-1.5 text-xs rounded-lg border outline-none bg-gray-800 border-gray-600 text-gray-100 focus:border-emerald-500';

function ConnectorForm({ editingProfile, colorOffset, isProvider, isConsumer, isFirst, onSave, onDelete, onCancel }) {
  const [name, setName] = useState(editingProfile?.name || '');
  const [providerUrl, setProviderUrl] = useState(editingProfile?.providerEdcUrl || '');
  const [consumerUrl, setConsumerUrl] = useState(editingProfile?.consumerEdcUrl || '');
  const [providerDspUrl, setProviderDspUrl] = useState(editingProfile?.providerDspUrl || '');
  const [consumerDspUrl, setConsumerDspUrl] = useState(editingProfile?.consumerDspUrl || '');
  const [apiKey, setApiKey] = useState(editingProfile?.providerApiKey || '');
  const [colorIdx, setColorIdx] = useState(
    editingProfile ? (COLORS.findIndex(c => c.cls === editingProfile.color) ?? 0) : colorOffset % COLORS.length
  );

  return (
    <div className="fixed inset-x-4 top-16 z-50 mx-auto w-auto max-w-72 sm:inset-x-auto sm:right-72 sm:mx-0 sm:w-72 rounded-xl shadow-2xl p-4 border bg-gray-900 border-gray-700 max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-white">
          {editingProfile ? 'Edit Connector' : isFirst ? 'Set up your connector' : 'Add Another Connector'}
        </h3>
        <button onClick={onCancel} className="text-gray-400 hover:text-gray-300 transition-colors">
          <X size={16} />
        </button>
      </div>

      <div className="space-y-3">
        <div>
          <label className="text-xs mb-1 block text-gray-400">Name</label>
          <input type="text" value={name} onChange={e => setName(e.target.value)}
            placeholder="e.g. TRUE Connector" className={inputCls} autoFocus />
        </div>

        <div>
          <label className="text-xs mb-1 block text-gray-400">Color</label>
          <div className="flex gap-1.5 flex-wrap">
            {COLORS.map((c, i) => (
              <button key={c.cls} onClick={() => setColorIdx(i)}
                className={`w-5 h-5 rounded-full transition-transform ${c.cls} ${colorIdx === i ? 'ring-2 ring-white ring-offset-1 ring-offset-gray-900 scale-110' : 'hover:scale-110'}`}
              />
            ))}
          </div>
        </div>

        {isProvider && (
          <>
            <div>
              <label className="text-xs mb-1 block text-gray-400">Provider Management API URL</label>
              <input type="text" value={providerUrl} onChange={e => setProviderUrl(e.target.value)}
                placeholder="https://..." className={inputCls} />
            </div>
            <div>
              <label className="text-xs mb-1 block text-gray-400">Provider DSP URL <span className="opacity-50">(required for catalog/negotiation)</span></label>
              <input type="text" value={providerDspUrl} onChange={e => setProviderDspUrl(e.target.value)}
                placeholder="https://.../api/dsp" className={inputCls} />
            </div>
          </>
        )}

        {isConsumer && (
          <div>
            <label className="text-xs mb-1 block text-gray-400">Consumer Management API URL</label>
            <input type="text" value={consumerUrl} onChange={e => setConsumerUrl(e.target.value)}
              placeholder="https://..." className={inputCls} />
          </div>
        )}

        <div>
          <label className="text-xs mb-1 block text-gray-400">API Key <span className="opacity-50">(optional)</span></label>
          <input type="password" value={apiKey} onChange={e => setApiKey(e.target.value)} className={inputCls} />
        </div>

        <div className="flex items-center justify-between pt-1">
          {editingProfile && (
            <button onClick={() => onDelete(editingProfile.id)}
              className="flex items-center gap-1 text-xs text-red-400 hover:text-red-300 transition-colors">
              <Trash2 size={12} /> Delete
            </button>
          )}
          <button onClick={() => onSave({ name, colorIdx, providerUrl, consumerUrl, apiKey, providerDspUrl, consumerDspUrl })}
            disabled={!name.trim()}
            className={`ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              name.trim() ? 'bg-emerald-600 hover:bg-emerald-500 text-white' : 'bg-gray-600 text-gray-400 cursor-not-allowed'
            }`}>
            <Save size={12} />
            {editingProfile ? 'Save Changes' : 'Add Connector'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Header({ sidebarOpen, setSidebarOpen }) {
  const { user, logout, notifications, addNotification, removeNotification, theme, toggleTheme, connectorKey, incrementConnectorKey, connectorFormOpen, setConnectorFormOpen } = useStore();
  const userRoles = user?.roles || [];
  const isProvider = userRoles.includes('data-provider') || userRoles.includes('admin') || userRoles.includes('realm-admin');
  const isConsumer = userRoles.includes('data-consumer') || userRoles.includes('admin') || userRoles.includes('realm-admin');
  const isLight = theme === 'light';
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [profiles, setProfiles] = useState(() => getProfiles());
  const [activeId, setActiveId] = useState(() => getActiveProfileId());
  const [remoteNotifications, setRemoteNotifications] = useState([]);
  const menuRef = useRef(null);
  const notificationsRef = useRef(null);

  // profiles/activeId were only ever read once at mount (useState lazy
  // initializer), so a sync that finished after that - e.g. right after
  // login - never showed up here even though Dashboard.jsx's own connector
  // status (memoized on this same connectorKey) updated correctly. Same
  // fix as Dashboard.jsx: re-read whenever connectorKey changes.
  useEffect(() => {
    setProfiles(getProfiles());
    setActiveId(getActiveProfileId());
  }, [connectorKey]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target) && !connectorFormOpen) {
        setUserMenuOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setNotificationsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [connectorFormOpen]);

  useEffect(() => {
    const fetchRemoteNotifications = () => {
      apiClient.get('/notifications')
        .then(res => setRemoteNotifications(res.data || []))
        .catch(() => { /* best-effort */ });
    };
    fetchRemoteNotifications();
    const interval = setInterval(fetchRemoteNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const markRemoteRead = (id) => {
    setRemoteNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    apiClient.post(`/notifications/${id}/read`).catch(() => { /* best-effort */ });
  };

  const removeRemoteNotification = (id) => {
    setRemoteNotifications(prev => prev.filter(n => n.id !== id));
    apiClient.delete(`/notifications/${id}`).catch(() => { /* best-effort */ });
  };

  const clearAllNotifications = () => {
    notifications.forEach(n => removeNotification(n.id));
    if (remoteNotifications.length > 0) {
      setRemoteNotifications([]);
      apiClient.delete('/notifications').catch(() => { /* best-effort */ });
    }
  };

  const unreadRemoteCount = remoteNotifications.filter(n => !n.read).length;

  const handleLogout = () => {
    ['kc_token', 'kc_refresh_token', 'username', 'userRoles']
      .forEach(key => localStorage.removeItem(key));
    logout();
    navigate('/login');
  };

  const switchTo = (id) => {
    setActiveProfileId(id);
    setActiveId(id);
    incrementConnectorKey();
    const p = getProfiles().find(x => x.id === id);
    if (p) addNotification({ type: 'info', message: `Switched to connector: ${p.name}` });
  };

  const handleFormSave = ({ name, colorIdx, providerUrl, consumerUrl, apiKey, providerDspUrl, consumerDspUrl }) => {
    const id = editingProfile?.id || Date.now().toString();
    const profile = buildProfileData(id, name, colorIdx, providerUrl, consumerUrl, apiKey, providerDspUrl, consumerDspUrl);
    saveProfile(profile);
    registerAsDataPoolParticipant(profile);
    const updated = getProfiles();
    setProfiles(updated);
    const isEdit = !!editingProfile;
    if (!activeId || editingProfile?.id === activeId) switchTo(id);
    addNotification({ type: 'success', message: isEdit ? `Connector "${name}" updated.` : `Connector "${name}" added successfully!` });
    setConnectorFormOpen(false);
    setEditingProfile(null);
  };

  const handleFormDelete = (id) => {
    const deletedName = profiles.find(p => p.id === id)?.name || id;
    deleteProfile(id);
    const updated = getProfiles();
    setProfiles(updated);
    addNotification({ type: 'info', message: `Connector "${deletedName}" removed.` });
    if (activeId === id) {
      const next = updated[0]?.id || null;
      setActiveProfileId(next);
      setActiveId(next);
      incrementConnectorKey();
    }
    setConnectorFormOpen(false);
    setEditingProfile(null);
  };

  const openAdd = () => {
    setEditingProfile(null);
    setConnectorFormOpen(true);
  };

  const openEdit = (profile) => {
    setEditingProfile(profile);
    setConnectorFormOpen(true);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'success': return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'error':   return <AlertTriangle className="w-5 h-5 text-red-400" />;
      default:        return <Info className="w-5 h-5 text-blue-400" />;
    }
  };

  const getNotificationStyles = (type, light) => {
    switch (type) {
      case 'success': return light ? 'bg-green-50 border-green-200' : 'bg-green-900/20 border-green-800';
      case 'error':   return light ? 'bg-red-50 border-red-200' : 'bg-red-900/20 border-red-800';
      default:        return light ? 'bg-blue-50 border-blue-200' : 'bg-blue-900/20 border-blue-800';
    }
  };

  return (
    <>
      <header className="header-chrome bg-gray-900 border-b border-gray-800 sticky top-0 z-30">
        <div className="flex items-center justify-between px-3 sm:px-6 py-4 gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <button onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors lg:hidden flex-shrink-0">
              <Menu className="w-6 h-6 text-gray-400" />
            </button>
            <div className="min-w-0">
              <h2 className="text-base sm:text-lg font-semibold text-white truncate">ENACT Dataspace Management</h2>
              <p className="hidden sm:block text-sm text-gray-400 truncate">Secure data exchange platform</p>
            </div>
          </div>

          <div className="flex items-center gap-1 sm:gap-4 flex-shrink-0">
            <button onClick={toggleTheme} title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors">
              {theme === 'dark' ? <Sun className="w-5 h-5 text-gray-400" /> : <Moon className="w-5 h-5 text-gray-400" />}
            </button>

            {/* Notifications */}
            <div className="relative" ref={notificationsRef}>
              <button onClick={() => setNotificationsOpen(!notificationsOpen)}
                className="relative p-2 hover:bg-gray-800 rounded-lg transition-colors">
                <Bell className="w-5 h-5 text-gray-400" />
                {(notifications.length + unreadRemoteCount) > 0 && (
                  <span className="absolute top-1 right-1 flex items-center justify-center w-4 h-4 text-xs bg-red-500 text-white rounded-full">
                    {notifications.length + unreadRemoteCount}
                  </span>
                )}
              </button>
              {notificationsOpen && (
                <div className={`fixed inset-x-4 top-16 sm:absolute sm:inset-x-auto sm:right-0 sm:top-auto sm:mt-2 w-auto sm:w-96 max-h-96 overflow-y-auto border rounded-lg shadow-xl animate-slide-down ${isLight ? 'bg-white border-gray-200' : 'bg-gray-900 border-gray-800'}`}>
                  <div className={`sticky top-0 px-4 py-3 border-b flex items-center justify-between ${isLight ? 'bg-white border-gray-200' : 'bg-gray-900 border-gray-800'}`}>
                    <h3 className={`text-sm font-semibold ${isLight ? 'text-gray-900' : 'text-white'}`}>Notifications</h3>
                    {(notifications.length > 0 || remoteNotifications.length > 0) && (
                      <button onClick={clearAllNotifications}
                        className={`text-xs transition-colors ${isLight ? 'text-gray-500 hover:text-gray-900' : 'text-gray-400 hover:text-white'}`}>Clear all</button>
                    )}
                  </div>
                  {remoteNotifications.length > 0 && (
                    <div className={`py-2 border-b ${isLight ? 'border-gray-200' : 'border-gray-800'}`}>
                      {remoteNotifications.map((n) => (
                        <div key={`remote-${n.id}`}
                          className={`px-4 py-3 border-b last:border-b-0 ${isLight ? 'border-gray-200' : 'border-gray-800'} ${n.read ? (isLight ? 'bg-white' : 'bg-gray-900') : (isLight ? 'bg-blue-50 border-blue-200' : 'bg-blue-900/20 border-blue-800')}`}>
                          <div className="flex items-start gap-3">
                            <Info className="w-5 h-5 text-blue-400 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm break-words ${isLight ? 'text-gray-900' : 'text-white'}`}>{n.message}</p>
                              <p className={`text-xs mt-1 ${isLight ? 'text-gray-500' : 'text-gray-500'}`}>
                                {new Date(n.created_at).toLocaleTimeString('en-GB')}
                              </p>
                            </div>
                            <div className="flex-shrink-0 flex items-center gap-1">
                              {!n.read && (
                                <button onClick={() => markRemoteRead(n.id)}
                                  className={`transition-colors ${isLight ? 'text-gray-500 hover:text-gray-900' : 'text-gray-400 hover:text-white'}`}
                                  title="Mark as read">
                                  <Check className="w-4 h-4" />
                                </button>
                              )}
                              <button onClick={() => removeRemoteNotification(n.id)}
                                className={`transition-colors ${isLight ? 'text-gray-500 hover:text-gray-900' : 'text-gray-400 hover:text-white'}`}
                                title="Dismiss">
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {notifications.length === 0 && remoteNotifications.length === 0 ? (
                    <div className="px-4 py-8 text-center">
                      <Bell className={`w-12 h-12 mx-auto mb-3 ${isLight ? 'text-gray-500' : 'text-gray-600'}`} />
                      <p className={`text-sm ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>No notifications</p>
                    </div>
                  ) : notifications.length > 0 && (
                    <div className="py-2">
                      {notifications.map((notification) => (
                        <div key={notification.id}
                          className={`px-4 py-3 border-b last:border-b-0 ${isLight ? 'border-gray-200' : 'border-gray-800'} ${getNotificationStyles(notification.type, isLight)}`}>
                          <div className="flex items-start gap-3">
                            {getNotificationIcon(notification.type)}
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm break-words ${isLight ? 'text-gray-900' : 'text-white'}`}>{notification.message}</p>
                              {notification.timestamp && (
                                <p className={`text-xs mt-1 ${isLight ? 'text-gray-500' : 'text-gray-500'}`}>
                                  {new Date(notification.timestamp).toLocaleTimeString('en-GB')}
                                </p>
                              )}
                            </div>
                            <button onClick={() => removeNotification(notification.id)}
                              className={`flex-shrink-0 transition-colors ${isLight ? 'text-gray-500 hover:text-gray-900' : 'text-gray-400 hover:text-white'}`}>
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* User menu */}
            <div className="relative" ref={menuRef}>
              <button onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-3 p-2 hover:bg-gray-800 rounded-lg transition-colors">
                <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
                <div className="text-left hidden md:block">
                  <p className="text-sm font-medium text-white">{user?.username || 'User'}</p>
                  <p className="text-xs text-gray-400">{user?.email || ''}</p>
                </div>
              </button>

              {userMenuOpen && (
                <div className={`fixed inset-x-4 top-16 sm:absolute sm:inset-x-auto sm:right-0 sm:top-auto sm:mt-2 w-auto sm:w-64 rounded-lg shadow-xl py-2 animate-slide-down border ${isLight ? 'bg-white border-gray-200' : 'bg-gray-900 border-gray-800'}`}>
                  {/* User info */}
                  <div className={`px-4 py-3 border-b ${isLight ? 'border-gray-200' : 'border-gray-800'}`}>
                    <p className={`text-sm font-medium ${isLight ? 'text-gray-900' : 'text-white'}`}>{user?.username || 'User'}</p>
                    <p className={`text-xs ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>{user?.email || ''}</p>
                    {user?.roles?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {user.roles.filter(r => ['data-provider', 'data-consumer', 'admin', 'realm-admin'].includes(r)).map(r => (
                          <span key={r} className={`text-xs px-1.5 py-0.5 rounded capitalize ${isLight ? 'bg-blue-100 text-blue-700' : 'bg-blue-600/30 text-blue-400'}`}>
                            {r === 'realm-admin' ? 'admin' : r.replace('data-', '')}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Connectors */}
                  <div className={`px-4 py-2 border-b ${isLight ? 'border-gray-200' : 'border-gray-800'}`}>
                    <p className="connector-section-label text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Connectors</p>
                    {profiles.length === 0 ? (
                      <p className="connector-item-inactive text-xs text-gray-500 italic mb-2">No saved connectors yet</p>
                    ) : (
                      <div className="space-y-1 mb-2">
                        {profiles.map(profile => {
                          const isActive = profile.id === activeId;
                          return (
                            <div key={profile.id} className="flex items-center gap-2 group">
                              <button onClick={() => switchTo(profile.id)}
                                className={`flex-1 flex items-center gap-2 px-2 py-1.5 text-sm transition-colors ${
                                  isActive ? 'connector-item-active' : 'connector-item-inactive'
                                }`}>
                                <span className={`connector-avatar-initials w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${profile.color}`}>
                                  {getInitials(profile.name)}
                                </span>
                                <span className="truncate font-medium">{profile.name}</span>
                                {isActive && <Check size={13} className="ml-auto text-emerald-400 flex-shrink-0" />}
                              </button>
                              <button onClick={() => openEdit(profile)}
                                className="connector-edit-icon opacity-0 group-hover:opacity-100 p-1 text-gray-500 hover:text-gray-300 transition-all">
                                <Settings size={12} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <button onClick={openAdd}
                      className={`connector-add-btn w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs transition-colors ${isLight ? 'hover:bg-gray-100' : 'hover:bg-gray-800 hover:text-white'}`}>
                      <div className="w-6 h-6 rounded-full border border-dashed border-gray-600 flex items-center justify-center flex-shrink-0">
                        <Plus size={12} />
                      </div>
                      Add another connector
                    </button>
                  </div>

                  <button onClick={() => { setUserMenuOpen(false); navigate('/settings'); }}
                    className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors ${isLight ? 'text-gray-700 hover:bg-gray-100' : 'text-gray-300 hover:bg-gray-800'}`}>
                    <Settings className="w-4 h-4" />
                    Settings
                  </button>
                  <button onClick={handleLogout}
                    className={`w-full flex items-center gap-3 px-4 py-2 text-sm text-red-400 transition-colors ${isLight ? 'hover:bg-gray-100' : 'hover:bg-gray-800'}`}>
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {connectorFormOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => { setConnectorFormOpen(false); setEditingProfile(null); }} />
          <ConnectorForm
            editingProfile={editingProfile}
            colorOffset={profiles.length}
            isProvider={isProvider}
            isConsumer={isConsumer}
            isFirst={profiles.length === 0 && !editingProfile}
            onSave={handleFormSave}
            onDelete={handleFormDelete}
            onCancel={() => { setConnectorFormOpen(false); setEditingProfile(null); }}
          />
        </>
      )}
    </>
  );
}
